import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { OverviewTournament } from '@/api/types';
import { formatCount, formatDate, formatDateTime, formatPercent } from '@/lib/format';
import { Badge } from '@/ui/Badge';
import { INPUT_CLASS } from '@/ui/Field';
import { Select } from '@/ui/Select';
import { SortableTh, Table, Td, Th } from '@/ui/Table';
import { useSort, type SortColumns } from '@/ui/use-sort';
import { tournamentLabel } from './tournament-label';

type TournamentSortKey = 'name' | 'starts' | 'ends' | 'links' | 'invited' | 'joined' | 'joinRate' | 'lastActivity';

const SORT_COLUMNS: SortColumns<OverviewTournament, TournamentSortKey> = {
  name: (tournament) => tournamentLabel(tournament).toLowerCase(),
  starts: (tournament) => tournament.startAt,
  ends: (tournament) => tournament.endAt,
  links: (tournament) => tournament.linksIssued,
  invited: (tournament) => tournament.playersInvited,
  joined: (tournament) => tournament.playersRedeemed,
  joinRate: (tournament) => tournament.redeemRate,
  lastActivity: (tournament) => tournament.lastEventAt,
};

function matchesSearch(tournament: OverviewTournament, needle: string): boolean {
  if (!needle) return true;
  return `${tournament.title} ${tournament.tournamentId}`.toLowerCase().includes(needle);
}

export function TournamentsTable({ tournaments }: { tournaments: OverviewTournament[] }) {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');

  const statuses = useMemo(
    () => [...new Set(tournaments.map((tournament) => tournament.status).filter((value) => value !== null))].sort(),
    [tournaments],
  );
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return tournaments.filter(
      (tournament) => (!status || tournament.status === status) && matchesSearch(tournament, needle),
    );
  }, [tournaments, search, status]);
  const { sorted: rows, sort, toggle } = useSort(filtered, SORT_COLUMNS, { key: 'lastActivity', direction: 'desc' });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <input
            type="search"
            aria-label="Filter tournaments"
            className={`${INPUT_CLASS} max-w-xs`}
            placeholder="Filter by name or id"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          {statuses.length > 1 && (
            <Select
              label="Status"
              value={status}
              options={[
                { value: '', label: 'All statuses' },
                ...statuses.map((value) => ({ value, label: value.toLowerCase() })),
              ]}
              onChange={setStatus}
            />
          )}
        </div>
        <p className="text-sm text-slate-500">
          {rows.length} of {tournaments.length} tournaments
        </p>
      </div>
      <Table>
        <thead>
          <tr>
            <SortableTh align="left" sortKey="name" sort={sort} onSort={toggle} first="asc">
              Tournament
            </SortableTh>
            <Th align="left">Status</Th>
            <SortableTh align="left" sortKey="starts" sort={sort} onSort={toggle}>
              Starts
            </SortableTh>
            <SortableTh align="left" sortKey="ends" sort={sort} onSort={toggle}>
              Ends
            </SortableTh>
            <SortableTh sortKey="links" sort={sort} onSort={toggle}>
              Links
            </SortableTh>
            <SortableTh sortKey="invited" sort={sort} onSort={toggle}>
              Invited
            </SortableTh>
            <SortableTh sortKey="joined" sort={sort} onSort={toggle}>
              Joined
            </SortableTh>
            <SortableTh sortKey="joinRate" sort={sort} onSort={toggle}>
              Join rate
            </SortableTh>
            <SortableTh align="left" sortKey="lastActivity" sort={sort} onSort={toggle}>
              Last activity
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <Td align="left" colSpan={9} className="text-slate-500">
                {tournaments.length === 0 ? 'No links have been issued yet.' : 'No tournament matches the filters.'}
              </Td>
            </tr>
          )}
          {rows.map((tournament) => (
            <tr key={tournament.tournamentId}>
              <Td align="left">
                <Link
                  to={`/tournaments/${encodeURIComponent(tournament.tournamentId)}`}
                  className="font-medium text-blue-700 hover:underline"
                >
                  {tournamentLabel(tournament)}
                </Link>
                {tournament.title && (
                  <span className="ml-2 font-mono text-xs text-slate-400">{tournament.tournamentId}</span>
                )}
              </Td>
              <Td align="left">
                <Badge value={tournament.status} />
              </Td>
              <Td align="left">{formatDate(tournament.startAt)}</Td>
              <Td align="left">{formatDate(tournament.endAt)}</Td>
              <Td>{formatCount(tournament.linksIssued)}</Td>
              <Td>{formatCount(tournament.playersInvited)}</Td>
              <Td>{formatCount(tournament.playersRedeemed)}</Td>
              <Td>{formatPercent(tournament.redeemRate)}</Td>
              <Td align="left">{formatDateTime(tournament.lastEventAt)}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
